import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import JudgeSignatureCanvas from "./JudgeSignatureCanvas";

// #732 — HUGE signature capture modal (~75% viewport width per the card AC)
// for a single judge. Shows the existing signature (short-lived presigned
// signatureUrl from listJudges) if present, with Replace/Delete, or the blank
// canvas to capture a new one. Save posts a base64 PNG data URL to
// putJudgeSignature (score-api, create/replace, idempotent, score_admin-gated).
export default function JudgeSignatureModal({ open, onClose, judge, onSaved }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();

  const [dataUrl, setDataUrl] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const hasExisting = !!judge?.signaturePresent && !!judge?.signatureUrl;
  const showCanvas = replacing || !hasExisting;

  const handleClose = () => {
    setDataUrl(null);
    setReplacing(false);
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!judge || !dataUrl) return;
    try {
      setSaving(true);
      setError(null);
      unwrap(await scoreApi.judges.putJudgeSignature(judge.id, { signature: dataUrl }));
      setDataUrl(null);
      setReplacing(false);
      onSaved?.();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!judge) return;
    try {
      setDeleting(true);
      setError(null);
      unwrap(await scoreApi.judges.deleteJudgeSignature(judge.id));
      setReplacing(false);
      onSaved?.();
    } catch (err) {
      setError(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      // AppLayout's AppBar (common-fe) is pinned at zIndex 1300 — MUI's default
      // Modal z-index too (Dialog is built on Modal) — so without bumping this
      // the app bar paints over the dialog. Same fix as RideHistoryDrawer.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      // ~75% viewport width per the card AC — the whole point is a big,
      // easy-to-sign-on surface (tablet/kiosk friendly), not a cramped dialog.
      PaperProps={{ sx: { width: "75vw", maxWidth: "75vw" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h5" component="span" color="text.primary">
          {judge
            ? t("judges.signatureModal.title", {
                name: userDisplayName(judge),
              })
            : ""}
        </Typography>
        <IconButton onClick={handleClose} aria-label={t("judges.signatureModal.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message || t("judges.signatureModal.saveError")}
          </Alert>
        )}

        {showCanvas ? (
          <JudgeSignatureCanvas onChange={setDataUrl} height={320} />
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("judges.signatureModal.existing")}
            </Typography>
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "#ffffff",
                p: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={judge.signatureUrl}
                alt={t("judges.signatureModal.existing")}
                sx={{ maxWidth: "100%", maxHeight: 320 }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Stack direction="row" spacing={1.5} sx={{ width: "100%", justifyContent: "space-between" }}>
          <Box>
            {hasExisting && !replacing && (
              <Button color="error" onClick={handleDelete} disabled={deleting}>
                {deleting ? t("judges.signatureModal.deleting") : t("judges.signatureModal.delete")}
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={1.5}>
            {hasExisting && !replacing ? (
              <Button
                variant="outlined"
                onClick={() => setReplacing(true)}
                sx={{
                  color: colors.foxOrange,
                  borderColor: colors.foxOrange,
                  "&:hover": { borderColor: colors.foxOrange, bgcolor: "action.hover" },
                }}
              >
                {t("judges.signatureModal.replace")}
              </Button>
            ) : (
              <>
                <Button onClick={handleClose} sx={{ color: "text.primary" }}>
                  {t("judges.signatureModal.cancel")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!dataUrl || saving}
                  sx={{
                    bgcolor: colors.foxOrange,
                    "&:hover": { bgcolor: colors.foxOrange, filter: "brightness(0.92)" },
                  }}
                >
                  {saving ? t("judges.signatureModal.saving") : t("judges.signatureModal.save")}
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
